import { FieldView } from '../../FieldEditor';
import {
  InternalApiScenario,
  InternalApiScenarioContentTestResultInformation,
  InternalApiScenarioTypeV2,
} from '../../../api-internal';
import { SalvButton, Size } from '@salvapp/ui-components';
import { assignParameterValues, humanizePeriod } from '../../../common/versioning/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import React from 'react';
import { useCreateScenarioVersion } from '../scenarioVersionActions';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import TestRunTriggeringSegments from './TestRunTriggeringSegments';

export const TestRunAttributesCard = ({
  testResult,
  scenario,
}: {
  testResult: InternalApiScenarioContentTestResultInformation;
  scenario: InternalApiScenario;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isPeriodic = scenario?.type === InternalApiScenarioTypeV2.PERIODIC;

  const { mutate: createScenarioVersion } = useCreateScenarioVersion();

  return (
    <>
      <div className='scenario-card'>
        <div className='header'>Testing attributes</div>
        <div className='content'>
          {testResult.testPeriodicInterval && (
            <FieldView
              label='Periodic interval'
              value={humanizePeriod(testResult.testPeriodicInterval.interval, testResult.testPeriodicInterval.timeUnit)}
            />
          )}

          <FieldView
            label='Parameters'
            value={
              testResult.testParameters?.length
                ? testResult.testParameters?.map((param) => (
                    <div
                      key={param.name}
                      className='text-secondary'
                      style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {param.displayName} <FontAwesomeIcon icon={faArrowRight} />
                      <span className='text-default'>
                        {assignParameterValues(`$${param.name}`, testResult.testParameters || [])}
                      </span>
                    </div>
                  ))
                : '-'
            }
          />
        </div>

        {!isPeriodic && (
          <div className='m-t-16'>
            <div className='header'>Trigger for</div>
            <div className='content'>
              <TestRunTriggeringSegments type={'INCLUDED'} entityType={scenario.entityType} testResult={testResult} />
              <TestRunTriggeringSegments type={'EXCLUDED'} entityType={scenario.entityType} testResult={testResult} />
            </div>
          </div>
        )}
      </div>
      <div className={'m-t-8'} style={{ display: 'flex', flexDirection: 'row-reverse' }}>
        <SalvButton
          label='Create new version with applied attributes'
          size={Size.SMALL}
          onClick={() => {
            dispatch(
              showConfirmationModal({
                title: 'Are you sure you want to create a duplicate DRAFT version with new attributes applied?',
                confirmationText: 'Yes, create',
                cancelText: 'No',
                onConfirm: () => {
                  createScenarioVersion({
                    scenarioId: scenario!.id,
                    internalApiScenarioVersionCommand: {
                      ...testResult!.versionInfo,
                      logic: testResult.versionInfo.logic || '',
                      parameters: testResult.testParameters || [],
                      segments: testResult.testSegments || [],
                      excludedSegments: testResult.testExcludedSegments || [],
                      ...(testResult?.testPeriodicInterval &&
                        testResult!.versionInfo.periodicScenarioAttributes && {
                          periodicScenarioAttributes: {
                            ...testResult!.versionInfo.periodicScenarioAttributes,
                            periodicInterval: testResult.testPeriodicInterval.interval,
                            periodicIntervalTimeUnit: testResult.testPeriodicInterval.timeUnit,
                          },
                        }),
                      note: `Test attributes applied from test run on version ${testResult.versionInfo.versionId}`,
                    },
                  });

                  navigate(`/scenarios/${scenario.id}`);
                },
              })
            );
          }}
        />
      </div>
    </>
  );
};
